import React, { useState, useMemo, useRef, useEffect } from "react";
import { Tabs } from "@shared/ui/tabs";
import {
  useSearchParams,
  useParams,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { PageHeader } from "@shared/components/PageHeader";
import { ArrowRight, Check, Info, Users, UserX } from "lucide-react";
import { getDataTypeConfig } from "./config/DataTypeConfig";
import {
  useRelationTabs,
  useAssignRelation,
  useFinalizeStage3,
} from "../../hooks";
import { GroupedDataTable } from "./sub-tabs/GroupDataTable";
import { NullRowsTable } from "./sub-tabs/NullRowTable";
import { ProgressBar } from "../ProgressBar";
import { Button } from "@shared/ui/button";
import { toast } from "@shared/hooks/use-toast";
import { getDataProgress } from "../../api";
import { ArrowLeft } from "lucide-react";
import { NavigationButtons } from "../shared/NavigationButtons";

const DataRelationMapping: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isFinalizingStage4, setIsFinalizingStage4] = useState(false);
  const [stage4Progress, setStage4Progress] = useState(0);
  const pollingInterval = useRef(null);

  const [nullAssignmentState, setNullAssignmentState] = useState<
    Record<
      string,
      Record<
        number,
        { targetId: number | string; targetName: string; timestamp: number }
      >
    >
  >({});

  const [groupedAssignmentState, setGroupedAssignmentState] = useState<
    Record<
      string,
      Record<
        string,
        { targetId: number | string; targetName: string; timestamp: number }
      >
    >
  >({});

  const finalizeStage3Mutation = useFinalizeStage3();

  const { selectedType, uploadId } = useParams<{
    selectedType: string;
    uploadId: string;
  }>();

  const sessionId = uploadId || location.state?.uploadId;
  const dataType = location.state?.selectedType || selectedType;
  const config = getDataTypeConfig(dataType);

  const [searchParams, setSearchParams] = useSearchParams();
  const [activeSubTab, setActiveSubTab] = useState<"grouped" | "null">(
    "grouped"
  );
  const [isDataReady, setIsDataReady] = useState(false);
  const [isCheckingProgress, setIsCheckingProgress] = useState(true);
  const initialPollingRef = useRef(null);

  const checkDataReadiness = async () => {
    try {
      const progressData = await getDataProgress(sessionId);

      if (
        progressData.percentage >= 100 &&
        progressData.status === "completed"
      ) {
        setIsDataReady(true);
        setIsCheckingProgress(false);
        if (initialPollingRef.current) {
          clearInterval(initialPollingRef.current);
        }
      }
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: error?.message,
      });
    }
  };

  useEffect(() => {
    if (sessionId) {
      setIsCheckingProgress(true);

      // Initial check
      checkDataReadiness();

      // Set up polling
      initialPollingRef.current = setInterval(() => {
        checkDataReadiness();
      }, 2000);
    }

    return () => {
      if (initialPollingRef.current) {
        clearInterval(initialPollingRef.current);
      }
    };
  }, [sessionId]);

  const { data: relationTabsData, isLoading: isLoadingTabs } = useRelationTabs(
    isDataReady ? sessionId : null // Only call when data is ready
  );

  const currentTab =
    searchParams.get("tab") || relationTabsData?.tabs[0]?.name || "plan";
  const currentTabData = relationTabsData?.tabs.find(
    (t) => t.name === currentTab
  );

  const assignMutation = useAssignRelation();

  // Add this BEFORE the DataRelationMapping component
  const RelationshipMappingInfoPanels = () => {
    const steps = [
      {
        title: "Review Invalid Records",
        description:
          "Address all records showing 'invalid' status (displayed in red)",
      },
      {
        title: "Map Missing Relationships",
        description: "Connect your data values to existing system options",
      },
      {
        title: "Use Bulk Assignment",
        description:
          "Efficiently assign multiple records to the same values when applicable",
      },
      {
        title: "Verify Auto-Mapped Data",
        description:
          "Confirm automatically recognized relationships are correct",
      },
      {
        title: "Check All Data Categories",
        description:
          "Review each tab to ensure no unmapped relationships remain",
      },
      {
        title: "Test Search & Filters",
        description:
          "Use these tools to quickly locate and fix problematic records",
      },
    ];

    if (isCheckingProgress || isLoadingTabs || !relationTabsData?.tabs) {
      return (
        <div className="space-y-6">
          <ProgressBar
            currentStep={4}
            totalSteps={7}
            progress={57}
            currentStepName="Data Relation Mapping"
          />
          <div className="bg-white">
            <PageHeader
              title="Data Relation Mapping"
              subtitle="Map source values to system values"
            />
          </div>
          <div className="p-8 text-center text-gray-500">
            {isCheckingProgress ? (
              <div className="flex flex-col items-center space-x-2">
                <svg
                  className="animate-spin h-8 w-8 text-blue-500 mb-4"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                  />
                </svg>
                <p>Preparing your data...</p>
              </div>
            ) : (
              "Loading relation tabs..."
            )}
          </div>
        </div>
      );
    }

    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Steps to Complete Panel */}
        <div className="bg-green-50 border border-green-200 rounded-lg p-6">
          <div className="flex items-center space-x-2 mb-4">
            <Check className="h-5 w-5 text-green-600" />
            <h3 className="font-semibold text-green-800">
              Complete These Steps to Continue
            </h3>
          </div>

          <div className="space-y-3 text-sm text-green-700">
            {steps.map((step, index) => (
              <div key={index} className="flex items-start space-x-2 pb-6">
                <div className="w-5 h-5 rounded-full bg-green-600 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Check className="h-3 w-3 text-white" />
                </div>
                <div>
                  <strong>{step.title}</strong> - {step.description}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Why Relationship Mapping Matters Panel */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <div className="flex items-center space-x-2 mb-4">
            <div className="flex items-center gap-2">
              <Info size={20} className="text-blue-600" />

              <h3 className="font-semibold text-blue-800">
                Why Relationship Mapping Matters
              </h3>
            </div>
          </div>

          <div className="text-sm text-blue-700 mb-4">
            This step connects your data to existing system records and
            maintains data integrity.
          </div>

          <div className="space-y-3 text-sm text-blue-700">
            <div>
              <strong>What happens here:</strong>
              <ul className="list-disc list-inside mt-1 space-y-1 ml-4">
                <li>
                  Your imported data is linked to pre-existing categories,
                  plans, statuses, and other reference data
                </li>
                <li>
                  Invalid or unrecognized values are flagged for your attention
                </li>
                <li>
                  The system attempts to automatically match your data with
                  existing options
                </li>
              </ul>
            </div>

            <div>
              <strong>What happens if relationships aren't mapped:</strong>
              <ul className="list-disc list-inside mt-1 space-y-1 ml-4">
                <li>
                  Records with invalid relationships will be rejected during
                  migration
                </li>
                <li>
                  Data may appear disconnected from important system features
                </li>
                <li>Reporting and filtering capabilities will be limited</li>
                <li>
                  User workflows that depend on these relationships will break
                </li>
              </ul>
            </div>

            <div className="bg-blue-100 rounded-md p-3 mt-4">
              <strong>Benefits of proper mapping:</strong>
              <ul className="list-disc list-inside mt-1 space-y-1 ml-2 text-xs">
                <li>Ensures data consistency across your system</li>
                <li>Enables powerful filtering and reporting capabilities</li>
                <li>Maintains referential integrity for reliable operations</li>
                <li>
                  Allows users to leverage existing system features and
                  workflows
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const handleGroupedAssign = (
    sourceValue: string,
    targetId: number,
    targetName: string
  ) => {
    if (!currentTabData) return;

    assignMutation.mutate({
      sessionId,
      payload: {
        tabName: currentTabData.targetField,
        sourceValue: sourceValue,
        targetId: targetId,
        targetName: targetName,
      },
    });
  };

  const handleNullAssign = (
    rowIndices: number[],
    targetId: number,
    targetName: string
  ) => {
    if (!currentTabData) return;

    assignMutation.mutate({
      sessionId,
      payload: {
        tabName: currentTabData.targetField,
        rowIndices: rowIndices,
        targetId: targetId,
        targetName: targetName,
      },
    });
  };
  const handlePageChange = (page: number) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set("page", String(page));
    setSearchParams(newParams);
  };

  // ✅ ADD: Handler for null row assignments
  const handleNullAssignmentChange = (
    tabName: string,
    assignments: Record<
      number,
      { targetId: number | string; targetName: string; timestamp: number }
    >
  ) => {
    setNullAssignmentState((prev) => ({
      ...prev,
      [tabName]: assignments,
    }));
  };

  // ✅ ADD: Handler for grouped assignments
  const handleGroupedAssignmentChange = (
    tabName: string,
    assignments: Record<
      string,
      { targetId: number | string; targetName: string; timestamp: number }
    >
  ) => {
    setGroupedAssignmentState((prev) => ({
      ...prev,
      [tabName]: assignments,
    }));
  };

  const getAllAssignmentsForRow = (rowIndex: number): Record<string, any> => {
    const allAssignments: Record<string, any> = {};

    // Loop through all tabs and collect assignments for this rowIndex
    Object.entries(nullAssignmentState).forEach(([tabName, assignments]) => {
      const assignment = assignments[rowIndex];
      if (assignment) {
        // Find the target field for this tab
        const tabData = relationTabsData?.tabs.find((t) => t.name === tabName);
        if (tabData) {
          allAssignments[tabData.targetField] = assignment.targetName;
        }
      }
    });

    return allAssignments;
  };

  const startStage4Polling = (sessionId: string) => {
    setIsFinalizingStage4(true);
    setStage4Progress(0);

    pollingInterval.current = setInterval(async () => {
      try {
        const progressData = await getDataProgress(sessionId);

        setStage4Progress(progressData.percentage);

        if (
          progressData.percentage >= 100 &&
          progressData.status === "completed"
        ) {
          clearInterval(pollingInterval.current);
          setIsFinalizingStage4(false);

          navigate(`/onboarding/data-migration/data-validation/${sessionId}`, {
            state: {
              sessionId,
              selectedType: dataType,
            },
          });
        }
      } catch (error) {
        clearInterval(pollingInterval.current);
        setIsFinalizingStage4(false);
        toast({
          variant: "destructive",
          title: "Error",
          description: error?.message || "Failed to get progress",
        });
      }
    }, 2000);
  };

  useEffect(() => {
    return () => {
      if (pollingInterval.current) {
        clearInterval(pollingInterval.current);
      }
    };
  }, []);

  const handleBack = () => {
    navigate(-1);
  };

  const handleContinue = async () => {
    if (!sessionId) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Session ID is missing. Please try again.",
      });
      return;
    }

    try {
      setIsFinalizingStage4(true);

      const result = await finalizeStage3Mutation.mutateAsync({
        sessionId,
        confirmed: true,
      });

      startStage4Polling(sessionId);
    } catch (error) {
      setIsFinalizingStage4(false);
      toast({
        variant: "destructive",
        title: "Error",
        description: error?.message || "Failed to finalize stage 3",
      });
    }
  };

  // ✅ Optimized tabComponents - only recreate when data actually changes
  const tabComponents = useMemo(() => {
    if (!relationTabsData?.tabs) return {};

    const components: Record<string, any> = {};

    relationTabsData.tabs.forEach((tab) => {
      components[tab.name] = {
        label: (
          <div className="flex items-center gap-2">
            <span>{tab.displayName}</span>
          </div>
        ),
        shortLabel: tab.displayName,
        component: (
          <div className="space-y-4">
            <div className="border-b border-gray-200">
              <nav className="flex gap-4">
                <button
                  className={`px-4 py-2 border-b-2 font-medium transition-colors ${
                    activeSubTab === "grouped"
                      ? "border-blue-600 text-blue-600"
                      : "border-transparent text-gray-500 hover:text-gray-700"
                  }`}
                  onClick={() => setActiveSubTab("grouped")}
                >
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4" />
                    <span>Grouped Data</span>
                  </div>
                </button>
                <button
                  className={`px-4 py-2 border-b-2 font-medium transition-colors ${
                    activeSubTab === "null"
                      ? "border-blue-600 text-blue-600"
                      : "border-transparent text-gray-500 hover:text-gray-700"
                  }`}
                  onClick={() => setActiveSubTab("null")}
                >
                  <div className="flex items-center gap-2">
                    <UserX className="h-4 w-4" />
                    <span>Null Values</span>
                  </div>
                </button>
              </nav>
            </div>

            <div className="">
              {activeSubTab === "grouped" ? (
                <GroupedDataTable
                  sessionId={sessionId}
                  dataType={dataType}
                  tabName={tab.name}
                  targetField={tab.targetField}
                  onAssign={handleGroupedAssign}
                  isAssigning={assignMutation.isPending}
                  // ✅ ADD: Pass lifted state
                  assignmentState={groupedAssignmentState[tab.name] || {}}
                  onAssignmentChange={(assignments) =>
                    handleGroupedAssignmentChange(tab.name, assignments)
                  }
                />
              ) : (
                <NullRowsTable
                  sessionId={sessionId}
                  dataType={dataType}
                  tabName={tab.name}
                  targetField={tab.targetField}
                  onPageChange={handlePageChange}
                  onAssign={handleNullAssign}
                  isAssigning={assignMutation.isPending}
                  // ✅ ADD: Pass lifted state
                  assignmentState={nullAssignmentState[tab.name] || {}}
                  onAssignmentChange={(assignments) =>
                    handleNullAssignmentChange(tab.name, assignments)
                  }
                  allTabsAssignmentState={nullAssignmentState}
                  relationTabs={relationTabsData?.tabs || []}
                />
              )}
            </div>
          </div>
        ),
      };
    });

    return components;
  }, [
    relationTabsData?.tabs,
    activeSubTab,
    sessionId,
    dataType,
    assignMutation.isPending,
    nullAssignmentState, // ✅ ADD: dependency
    groupedAssignmentState, // ✅ ADD: dependency
    currentTabData,
  ]);

  const urlMapping = useMemo(() => {
    if (!relationTabsData?.tabs) return {};

    const mapping: Record<string, string> = {};
    relationTabsData.tabs.forEach((tab) => {
      mapping[tab.name] = tab.name;
    });
    return mapping;
  }, [relationTabsData?.tabs]);

  if (isLoadingTabs || !relationTabsData?.tabs) {
    return (
      <div className="space-y-6">
        <ProgressBar
          currentStep={4}
          totalSteps={7}
          progress={57}
          currentStepName="Data Relation Mapping"
        />
        <div className="bg-white">
          <PageHeader
            title="Data Relation Mapping"
            subtitle="Map source values to system values"
          />
        </div>
        <div className="p-8 text-center text-gray-500">
          Loading relation tabs...
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <ProgressBar
        currentStep={4}
        totalSteps={7}
        progress={57}
        currentStepName="Data Relation Mapping"
      />
      <div className="bg-white">
        <PageHeader
          title="Data Relation Mapping"
          subtitle="Map source values to system values"
        />
      </div>

      <RelationshipMappingInfoPanels />

      <Tabs
        defaultValue={relationTabsData?.tabs[0]?.name || "plan"}
        urlMapping={urlMapping}
        tabComponents={tabComponents}
        tabsListClassName="grid w-full lg:w-auto grid-cols-3 lg:grid-cols-4"
        idPrefix="data-relation-mapping"
        disableUrlSync={false}
      />

      <NavigationButtons
        onBack={handleBack}
        onContinue={handleContinue}
        continueText="Continue"
        isLoading={isFinalizingStage4 || finalizeStage3Mutation.isPending}
        loadingText={`Processing... ${stage4Progress}%`}
        canContinue={true}
      />
    </div>
  );
};

export default DataRelationMapping;
